// 261005 E1-P1 — hộp "Đẩy sang Chapters": số CHÍNH XÁC từ `GET …/handoff-plan` (không ghi), rồi mới gọi POST handoff.
// Là Modal điều khiển bằng state (có tải, có lỗi) — không dùng Modal.confirm. 409 từ route → hộp lỗi có mã như cũ.
import React, { useState } from 'react';
import { Alert, Modal, Space, Typography, message } from 'antd';
import { adaptationApi } from '../../../services/api/adaptation';
import type { HandoffPlanPreview } from '../../../types/adaptationWorkbench';
import { describeAdaptationError, describeHandoffResult, HandoffResultView } from '../adaptationUtils';
import { useWorkbenchCtx } from './WorkbenchContext';
import { handoffHasWork, summarizeHandoffPlan } from './workbenchView';

const { Text } = Typography;

/** 261015 P5a (D7): hộp chỉ còn mở khi kế hoạch có tên gốc rò (`handoffClickDecision === 'MODAL'`); kế hoạch do nơi gọi đã GET (không GET lần hai). */
interface Props { plan: HandoffPlanPreview | null; onClose: () => void }

const HandoffModal: React.FC<Props> = ({ plan, onClose }) => {
  const { storyId, refreshAll, showError } = useWorkbenchCtx();
  const [pushing, setPushing] = useState(false);

  const s = summarizeHandoffPlan(plan);

  const push = async () => {
    setPushing(true);
    try {
      const r = await adaptationApi.handoff(storyId);
      const view = describeHandoffResult(r as HandoffResultView);
      if (view.level === 'warning') message.warning(view.text, 10);
      else message.success(view.text, 5);
      onClose();
      await refreshAll();
    } catch (e) {
      onClose();
      showError(describeAdaptationError(e));
    } finally { setPushing(false); }
  };

  return (
    <Modal
      open={!!plan} title="Đẩy sang Chapters" onCancel={onClose} width={560}
      okText="Đẩy" cancelText="Huỷ" onOk={push}
      okButtonProps={{ disabled: !plan || !handoffHasWork(s) || s.blocked.length > 0, loading: pushing }}
    >
      {plan && (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text>Sẽ <Text strong>tạo {s.created}</Text> · <Text strong>cập nhật {s.updated}</Text> chương.</Text>
          {(s.keptEdited > 0 || s.keptForPlan > 0 || s.unchanged > 0) && (
            <Text type="secondary">
              Giữ nguyên: {s.keptEdited} tập (đã sửa tay ở Chapters) · {s.keptForPlan} tập (đã có Film plan) · {s.unchanged} tập (không đổi).
            </Text>
          )}
          {s.autoSwapped.length > 0 && (
            <Text type="secondary">Chương tập {s.autoSwapped.join(', ')} sẽ được tự thay tên gốc (kịch bản ⑤ giữ nguyên).</Text>
          )}
          {!handoffHasWork(s) && s.blocked.length === 0 && <Alert type="info" showIcon message="Không có tập nào cần tạo hay cập nhật." />}
          {s.blocked.length > 0 && (
            <Alert
              type="error" showIcon message="Chưa đẩy được — còn tên gốc không tự thay được"
              description={(
                <>
                  {s.blocked.map((b) => <div key={b.episode}>Tập {b.episode}: {b.terms.join(', ')}</div>)}
                  <div>Mở tập ở ⑤ sửa tay chỗ đó, hoặc đổi tên mới ở ③, rồi đẩy lại.</div>
                </>
              )}
            />
          )}
          {s.warnLeaks.length > 0 && (
            <Alert
              type="warning" showIcon message="Còn tên gốc rò trong chương sẽ đẩy"
              description={s.warnLeaks.map((l) => <div key={l.episode}>Tập {l.episode}: {l.terms.join(', ')}</div>)}
            />
          )}
          <Text type="secondary" style={{ fontSize: 13 }}>Số liệu vừa lấy từ máy chủ; máy chủ vẫn tự tính lại và kiểm cổng khi bấm Đẩy.</Text>
        </Space>
      )}
    </Modal>
  );
};

export default HandoffModal;
